import type { BuilderContent } from '@builder.io/sdk'

import { log } from '@src/utils/log.util'

/**
 * Recursively finds elements with 'navigate' actionName that have children with the same 'navigate',
 * then changes the children's actionName to 'none' to prevent nested anchor tags.
 *
 * Fixes "In HTML, <a> cannot be a descendant of <a>. This will cause a hydration error."
 *
 * @param content Builder content to process
 * @returns The processed content with removed nested anchor tags
 */
export const removeNestedLinkElements = (content: BuilderContent) => {
  if (!content || !content.data) return content

  const processElement = (element: any): any => {
    // Skip if element doesn't exist
    if (!element) return element

    // Check if this element has the 'navigate' actionName
    const hasNavigateAction = element.component?.options?.actionName === 'navigate'

    // Process children recursively, regardless of this element's action
    if (Array.isArray(element.children)) {
      const updatedChildren = element.children.map((child: any) => {
        // If parent has navigate action and child also has navigate action
        if (hasNavigateAction && child.component?.options?.actionName === 'navigate') {
          // Log a warning about nested navigate actions
          log.warn(
            `[Builder] Detected nested navigation actions in component. Parent and child both have 'navigate' action.`,
            {
              parent: { name: element.component?.name, options: element.component?.options },
              child: { name: child.component?.name, options: child.component?.options },
              path: child.id ? `element-${child.id}` : 'unknown-path',
              pageTitle: content.data?.title,
              pageCanonicalUrl: content.data?.canonicalUrl,
            }
          )
          // Clone the child and update its actionName to 'none'
          const updatedChild = { ...child }
          if (updatedChild.component && updatedChild.component.options) {
            updatedChild.component = {
              ...updatedChild.component,
              options: {
                ...updatedChild.component.options,
                actionName: 'none',
              },
            }
          }
          return processElement(updatedChild)
        }

        // Process child normally
        return processElement(child)
      })

      return { ...element, children: updatedChildren }
    }

    // For elements with nested blocks (top-level content structures)
    if (element.blocks && Array.isArray(element.blocks)) {
      return {
        ...element,
        blocks: element.blocks.map((block: any) => processElement(block)),
      }
    }

    return element
  }

  // Create a copy of the content to avoid mutating the original
  const processedContent = JSON.parse(JSON.stringify(content)) as typeof content

  // Process the content structure
  // Builder content data can have different structures
  // Safely process data.blocks if it exists
  if (processedContent.data) {
    const data = processedContent.data as any
    if (data.blocks && Array.isArray(data.blocks)) {
      data.blocks = data.blocks.map(processElement)
    }
  }

  return processedContent
}
